import { HubConnectionBuilder, HubConnectionState, LogLevel, HttpError } from '@microsoft/signalr';
import { applicationStore } from '../../store/applicationStore';
import { CommonConnectionsMethod, GameLiveConnectionMethod } from './types';
import { userInfoStore } from '../../store/userInfoStore';
import { gameMainDrawer } from '../../game/drawers/gameMainDrawer';
import { parseQuery } from '../../helpers/parseQuery';
import { ValidationType } from '../../enums/validationType';
import { spinBarStore } from '../../store/spinBarStore';
import { DefaultQueryParams } from '../../constants/defaultQueryParams';
import { errorCodes } from '../../constants/errorCodes';
import { handleErrorCodesPostMessage } from '../../helpers/handleErrorCodesPostMessage';
import { gameService } from '../GameService';

export class BaseLiveConnection {
  connection;

  connectionStartPromise;

  connectionStopPromise;

  url;

  isOnReconnectingState;

  constructor(url) {
    this.url = url;
  }

  async waitStarted() {
    if (this.connectionStartPromise instanceof Promise) {
      await this.connectionStartPromise;
      this.connectionStartPromise = null;
    }
  }

  async waitStop() {
    if (this.connectionStopPromise instanceof Promise) {
      await this.connectionStopPromise;
      this.connectionStopPromise = null;
    }
  }

  async init(reconnectTimes = [300, 600]) {
    await this.waitStop();
    await this.waitStarted();
    if (this.connection?.state !== HubConnectionState.Connected) {
      this.connection = this.createConnection(reconnectTimes);
      this.connectionStartPromise = this.connection.start();
      await this.waitStarted();
    }
    return this.connection;
  }

  createConnection(reconnectTimes = [300, 15000]) {
    const { accessToken } = applicationStore;
    return new HubConnectionBuilder()
      .withUrl(this.url, { accessTokenFactory: () => accessToken })
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect(reconnectTimes)
      .build();
  }

  async invoke(methodName, ...args) {
    await this.waitStarted();
    if (this.connection.state === HubConnectionState.Connected) {
      try {
        return this.connection.invoke(methodName, ...args).catch(() => {
          gameService.sendLauncherErrorCode();
        });
      } catch (err) {
        gameService.sendLauncherErrorCode();
      }
    }
    return null;
  }

  on(methodName, handler) {
    if (this.connection.state === HubConnectionState.Connected)
      this.connection.on(methodName, handler);
    return this;
  }

  onReconnecting(callback) {
    if (this.connection) {
      this.connection.onreconnecting((data) => {
        if (callback) {
          callback(data);
        }
        this.isOnReconnectingState = true;
        this.callbackMiddleware({
          methodName: CommonConnectionsMethod.Reconnecting,
        });
      });
    }
    return null;
  }

  onClose(callback) {
    if (this.connection && this.connection.state === HubConnectionState.Connected) {
      return this.connection.onclose((data) => {
        if (callback) {
          callback(data);
        }

        if (this.isOnReconnectingState) {
          this.callbackMiddleware({
            methodName: CommonConnectionsMethod.ConnectionClose,
          });
        }
      });
    }
    return null;
  }

  onReconnected(callback) {
    if (this.connection) {
      return this.connection.onreconnected((data) => {
        if (callback) {
          callback(data);
        }

        this.isOnReconnectingState = false;
        this.callbackMiddleware({
          methodName: CommonConnectionsMethod.Reconnected,
        });
      });
    }
    return null;
  }

  async stop() {
    await this.waitStarted();
    await this.waitStop();
    if (this.connection?.state === HubConnectionState.Connected) {
      this.connectionStopPromise = this.connection.stop().then(() => {
        this.connectionStartPromise = null;
      });
      await this.waitStop();
    }
  }

  callbackMiddleware({ data, methodName }) {
    const methods = {
      [GameLiveConnectionMethod.GetBalanceResult]: (arg) => {
        if (arg.validation === ValidationType.Success) {
          userInfoStore.setUserBalance(arg.balance);
          gameMainDrawer.menuBar.updateUserBalance(spinBarStore.tournamentId);
        }
      },
      [GameLiveConnectionMethod.GetTournamentBalanceResult]: (arg) => {
        if (arg.validation === ValidationType.Success) {
          userInfoStore.setUserBalance(arg.balance);
          gameMainDrawer.menuBar.updateUserBalance();
        }
      },
      [GameLiveConnectionMethod.GameOpen]: (arg) => {
        const query = parseQuery(window.location.search);
        const { gameid } = query;
        if (this.connection?.connectionId !== arg.connectionId && arg.gameId === +gameid) {
          const { GameIsOpen } = errorCodes;
          this.stop();
          // jackpotConnection.stop();
          applicationStore.setIsConnectionLost(true);
          // gameMainDrawer.popupBar.togglePopupVisibility(true);
          // gameMainDrawer.popupBar.setGameOpen();
          handleErrorCodesPostMessage(GameIsOpen);
          gameMainDrawer.popupBar.clearReconnectAnimation();
          applicationStore.setIsJackPotOpacity(true);
        }
      },
      [CommonConnectionsMethod.ConnectionClose]: () => {
        // gameMainDrawer.popupBar.togglePopupVisibility(true);
        gameService.sendLauncherErrorCode();
        applicationStore.setIsJackPotOpacity(true);
      },
      [GameLiveConnectionMethod.Error]: () => {
        if (data instanceof HttpError || data.status === 401) {
          gameService.sendLauncherErrorCode();
          // gameMainDrawer.popupBar.togglePopupVisibility(true);
          // gameMainDrawer.popupBar.setConnectionLost();
          gameMainDrawer.popupBar.clearReconnectAnimation();
          applicationStore.setIsJackPotOpacity(true);
        }
      },
      [CommonConnectionsMethod.Reconnecting]: () => {
        const { Reconnect } = errorCodes;
        handleErrorCodesPostMessage(Reconnect);
        gameMainDrawer.popupBar.togglePopupVisibility(true);
        gameMainDrawer.popupBar.setReconnectAnimation();
        applicationStore.setIsJackPotOpacity(true);
        spinBarStore.setIsSpinStart(true);
        gameMainDrawer.spinBar.hideAutoBetPopup();
      },
      [CommonConnectionsMethod.Reconnected]: () => {
        gameMainDrawer.popupBar.clearReconnectAnimation();
        gameMainDrawer.popupBar.togglePopupVisibility(false);
        applicationStore.setIsJackPotOpacity(false);
      },
      [CommonConnectionsMethod.UnderMaintenance]: (arg) => {
        gameMainDrawer.notificationBar.startUnderMaintenanceFunctional(arg);
      },
    };
    methods[methodName]?.(data);
  }
}
